import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { initialComments, trainingVideos } from '../trainingData';
import TrainingCard from './TrainingCard';

function VideoPage({ video, progress, onProgress, t, onBack, onOpenVideo }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState(() => initialComments.filter((item) => item.videoId === video.id));
  const [checkedItems, setCheckedItems] = useState([]);
  const currentPercent = progress?.percentWatched || 0;
  const isComplete = Boolean(progress?.completedAt);

  useEffect(() => {
    if (!isPlaying || isComplete) return undefined;
    const timer = window.setInterval(() => {
      onProgress(video.id, Math.min(99, (progress?.percentWatched || 0) + 1));
    }, 900);
    return () => window.clearInterval(timer);
  }, [isPlaying, isComplete, onProgress, progress, video.id]);

  const related = useMemo(
    () => trainingVideos.filter((item) => item.id !== video.id && item.category === video.category).slice(0, 3),
    [video]
  );

  const nextVideo = trainingVideos.find(
    (item) => item.pathId === video.pathId && item.orderInPath === video.orderInPath + 1
  );

  const toggleAction = (index) => {
    setCheckedItems((items) => items.includes(index)
      ? items.filter((item) => item !== index)
      : [...items, index]);
  };

  const postComment = () => {
    if (!commentText.trim()) return;
    setComments((items) => [
      ...items,
      {
        id: `local-${Date.now()}`,
        videoId: video.id,
        userId: 'usr-001',
        name: 'Sofía Martínez',
        avatar: 'SM',
        text: commentText.trim(),
        createdAt: 'Just now'
      }
    ]);
    setCommentText('');
  };

  return (
    <motion.div className="page-content video-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <button type="button" className="back-button" onClick={onBack}>
        <SafeIcon icon={FiIcons.FiArrowLeft} /> {t.backToTraining}
      </button>

      <div className="video-layout">
        <div className="video-main-column">
          <div className="player-shell">
            {/* Replace this demo HTML5 player with the real Zoom, Vimeo, or provider player. */}
            <video
              controls
              poster={video.thumbnailUrl}
              src={video.videoUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => onProgress(video.id, 100, true)}
            />
            <p>{t.videoPlayerNote}</p>
          </div>

          <div className="video-heading">
            <div>
              <div className="training-card-topline">
                <span className="training-category">{video.category}</span>
                <span className={`language-badge ${video.language.toLowerCase()}`}>{video.language}</span>
              </div>
              <h1>{video.title}</h1>
              <p>{t.trainer}: {video.trainer} · {t.published} {video.publishedAt}</p>
            </div>
            <div className="watch-progress">
              <strong>{currentPercent}%</strong>
              <span>{t.watched}</span>
            </div>
          </div>

          <div className="video-progress-large">
            <span style={{ width: `${currentPercent}%` }} />
          </div>

          <div className="video-actions">
            <button
              type="button"
              className={`primary-button ${isComplete ? 'complete-button' : ''}`}
              onClick={() => onProgress(video.id, 100, true)}
            >
              <SafeIcon icon={isComplete ? FiIcons.FiCheckCircle : FiIcons.FiCheck} />
              {isComplete ? t.completedLabel : t.markComplete}
            </button>
          </div>

          <section className="video-info-card">
            <h2>{t.description}</h2>
            <p>{video.description}</p>
          </section>

          <section className="video-accordion">
            <button type="button" onClick={() => setShowTranscript((value) => !value)}>
              <span><SafeIcon icon={FiIcons.FiAlignLeft} /> {t.transcript}</span>
              <SafeIcon icon={showTranscript ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} />
            </button>
            {showTranscript && <p>{video.transcript}</p>}
          </section>

          <section className="video-info-card">
            <h2>{t.actionItems}</h2>
            <div className="action-list">
              {video.actionItems.map((item, index) => (
                <label key={item}>
                  <input type="checkbox" checked={checkedItems.includes(index)} onChange={() => toggleAction(index)} />
                  <span className={checkedItems.includes(index) ? 'checked-action' : ''}>{item}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="comments-card">
            <h2>{t.comments} <span>{comments.length}</span></h2>
            <div className="comment-composer">
              <span className="avatar avatar-small">SM</span>
              <textarea
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                placeholder={t.commentPlaceholder}
                aria-label={t.addComment}
                rows="2"
              />
              <button type="button" className="primary-button" onClick={postComment}>{t.postComment}</button>
            </div>
            <div className="comment-list">
              {comments.length ? comments.map((comment) => (
                <div className="comment" key={comment.id}>
                  <span className="avatar avatar-small">{comment.avatar}</span>
                  <div>
                    <strong>{comment.name}</strong>
                    <small>{comment.createdAt}</small>
                    <p>{comment.text}</p>
                  </div>
                </div>
              )) : (
                <div className="no-comments">{t.noComments}<small>{t.beFirstToComment}</small></div>
              )}
            </div>
          </section>
        </div>

        <aside className="video-side-column">
          {nextVideo && (
            <section className="next-video-card">
              <div className="section-heading">
                <h2>{t.nextInPath}</h2>
              </div>
              <TrainingCard video={nextVideo} progress={{}} t={t} onOpen={onOpenVideo} />
            </section>
          )}
          <section className="related-section">
            <div className="section-heading"><h2>{t.relatedVideos}</h2></div>
            <div className="related-list">
              {related.map((item) => (
                <TrainingCard key={item.id} video={item} progress={{}} t={t} onOpen={onOpenVideo} />
              ))}
            </div>
          </section>
        </aside>
      </div>
    </motion.div>
  );
}

export default VideoPage;